import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import SEOHead from "@/components/base/SEOHead";
import { Link } from "react-router-dom";
import ScrollReveal from "@/components/base/ScrollReveal";

const painPoints = [
  "有存款或投資，但沒有整體配置邏輯",
  "市場波動時容易追高殺低",
  "家庭現金流、保費、投資金額沒有分層",
  "不知道自己能承受多少風險",
  "資產增加了，但風險也跟著放大",
  "缺少定期檢視與再平衡機制",
];

const coreServices = [
  {
    number: "01",
    title: "資產盤點",
    desc: "整理收入、支出、負債、保費、投資與預備金。",
    icon: "ri-funds-line",
  },
  {
    number: "02",
    title: "資金分艙",
    desc: "分成生活帳戶、緊急預備金、保障帳戶、投資帳戶、長期目標帳戶。",
    icon: "ri-archive-drawer-line",
  },
  {
    number: "03",
    title: "風險控管與再平衡",
    desc: "建立資產比例、停損停利、定期檢視與再平衡規則。",
    icon: "ri-scales-3-line",
  },
];

const steps = [
  {
    number: "01",
    title: "初談釐清",
    desc: "了解收支現況、投資習慣、風險承受度與目標。",
  },
  {
    number: "02",
    title: "資產盤點",
    desc: "建立資產總表、現金流地圖與風險缺口。",
  },
  {
    number: "03",
    title: "分艙配置",
    desc: "設計各帳戶比例與再平衡規則。",
  },
  {
    number: "04",
    title: "定期追蹤",
    desc: "每季或半年檢視配置與再平衡。",
  },
];

export default function Asset() {
  return (
    <div className="min-h-screen bg-brand-black">
      <SEOHead
        title="財產門神 | 資產配置與風險控管 | 保險門神"
        description="財產門神專注於資產配置與風險控管，包含資產盤點、資金分艙、風險承受度評估、資產配置再平衡與現金流管理。先守風險，後追報酬。"
        keywords="資產配置,風險控管,資金分艙,財務紀律,現金流規劃,保險門神"
        canonicalPath="/services/asset"
        ogType="website"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "財產門神 - 資產配置與風險控管",
          "description": "資產盤點、資金分艙、風險承受度評估與再平衡規則，建立更穩定的財務系統。先守風險，後追報酬。",
          "provider": {
            "@type": "FinancialService",
            "name": "保險門神 Insurance Guardian",
            "url": "__SITE_URL__/"
          },
          "url": "__SITE_URL__/services/asset",
          "areaServed": { "@type": "Country", "name": "Taiwan" },
          "serviceType": "資產配置與財務風險控管"
        }}
      />
      <Header />
      <main>
        {/* Hero */}
        <section className="relative pt-24 md:pt-32 pb-12 md:pb-16 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20dark%20luxury%20financial%20background%20with%20golden%20flowing%20data%20streams%20and%20wealth%20symbols%20on%20deep%20black%20canvas%2C%20subtle%20stock%20chart%20patterns%20made%20of%20gold%20light%2C%20premium%20asset%20management%20brand%20aesthetic%2C%20sophisticated%20minimalist%20composition%20with%20metallic%20gold%20accents%2C%204K%20quality&width=1920&height=600&seq=103&orientation=landscape"
              alt="財產門神 Hero 背景"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40" />
          </div>
          <div className="container-main relative z-10">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-[0.9375rem] mb-6">
              <Link to="/" className="text-brand-text-secondary hover:text-brand-gold transition-colors">
                首頁
              </Link>
              <i className="ri-arrow-right-s-line text-brand-text-secondary" />
              <Link to="/services" className="text-brand-text-secondary hover:text-brand-gold transition-colors">
                服務項目
              </Link>
              <i className="ri-arrow-right-s-line text-brand-text-secondary" />
              <span className="text-brand-gold">財產門神</span>
            </div>

            <h1 className="text-white font-display font-bold text-[2rem] md:text-[2.75rem] leading-[1.3] md:leading-[1.25] mb-4">
              財產門神
            </h1>
            <p className="text-brand-gold text-[1.1875rem] font-medium mb-4">
              資產配置・風險控管
            </p>
            <p className="text-brand-text-secondary text-[1.1875rem] leading-[1.75] max-w-[800px]">
              先守風險，後追報酬。透過資產盤點、資金分艙、風險承受度評估與再平衡規則，建立更穩定的財務系統。
            </p>
          </div>
        </section>

        {/* Pain Points */}
        <section className="py-16 md:py-20 bg-brand-card border-y border-brand-border">
          <div className="container-main">
            <ScrollReveal>
              <div className="flex items-center gap-3 mb-10 md:mb-12">
                <div className="w-1 h-8 bg-brand-gold rounded-full" />
                <h2 className="text-white font-display font-bold text-[1.5rem] md:text-[2.125rem] leading-[1.4]">
                  常見痛點
                </h2>
              </div>
            </ScrollReveal>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {painPoints.map((point, index) => (
                <ScrollReveal key={index} delay={index * 80}>
                  <div
                    className="bg-brand-black border border-brand-border rounded-xl p-5 md:p-6 transition-all duration-300 hover:border-brand-gold/50 h-full"
                  >
                    <div className="flex items-start gap-4">
                      <span className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-brand-gold/15 text-brand-gold font-bold text-[0.9375rem] md:text-[1rem] shrink-0">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <p className="text-brand-text-secondary text-[1.0625rem] md:text-[1.1875rem] leading-[1.7] pt-1">
                        {point}
                      </p>
                    </div>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* Core Services Banner */}
        <section className="relative h-[260px] md:h-[340px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20dark%20luxury%20background%20with%20golden%20financial%20data%20streams%20and%20wealth%20symbols%20flowing%20on%20deep%20black%20canvas%2C%20subtle%20stock%20chart%20and%20pie%20chart%20patterns%20made%20of%20gold%20metallic%20light%2C%20premium%20asset%20management%20brand%20aesthetic%2C%20sophisticated%20minimalist%20composition%20with%20warm%20golden%20accents%2C%20cinematic%20lighting%2C%204K%20quality&width=1920&height=400&seq=202&orientation=landscape"
            alt="財產核心服務意象"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/30 to-black/70" />
        </section>

        {/* Core Services */}
        <section className="py-16 md:py-20">
          <div className="container-main">
            <ScrollReveal>
              <div className="flex items-center gap-3 mb-10 md:mb-12">
                <div className="w-1 h-8 bg-brand-gold rounded-full" />
                <h2 className="text-white font-display font-bold text-[1.5rem] md:text-[2.125rem] leading-[1.4]">
                  三大核心服務
                </h2>
              </div>
            </ScrollReveal>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {coreServices.map((service, index) => (
                <ScrollReveal key={service.number} delay={index * 120}>
                  <div
                    className="bg-brand-card border border-brand-border rounded-2xl p-6 md:p-8 transition-all duration-300 hover:border-brand-gold/50 h-full"
                  >
                    <div className="flex items-center gap-4 mb-5">
                      <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-brand-gold/10">
                        <span className="w-6 h-6 flex items-center justify-center text-brand-gold text-xl">
                          <i className={service.icon} />
                        </span>
                      </div>
                      <span className="text-brand-gold font-bold text-[1rem] md:text-[1.125rem]">
                        {service.number}
                      </span>
                    </div>
                    <h3 className="text-white font-semibold text-[1.25rem] md:text-[1.625rem] leading-[1.45] mb-3">
                      {service.title}
                    </h3>
                    <p className="text-brand-text-secondary text-[1.0625rem] md:text-[1.1875rem] leading-[1.75]">
                      {service.desc}
                    </p>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="py-16 md:py-20 bg-brand-card border-y border-brand-border">
          <div className="container-main">
            <ScrollReveal>
              <div className="flex items-center gap-3 mb-10 md:mb-12">
                <div className="w-1 h-8 bg-brand-gold rounded-full" />
                <h2 className="text-white font-display font-bold text-[1.5rem] md:text-[2.125rem] leading-[1.4]">
                  合作流程
                </h2>
              </div>
            </ScrollReveal>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {steps.map((step, index) => (
                <ScrollReveal key={step.number} delay={index * 100}>
                  <div
                    className="bg-brand-black border border-brand-border rounded-xl p-5 md:p-6 transition-all duration-300 hover:border-brand-gold/50 h-full"
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-brand-gold/20 border border-brand-gold flex items-center justify-center shrink-0">
                        <span className="text-brand-gold font-bold text-[0.9375rem] md:text-[1.0625rem]">
                          {step.number}
                        </span>
                      </div>
                      <h3 className="text-white font-semibold text-[1.125rem] md:text-[1.25rem]">
                        {step.title}
                      </h3>
                    </div>
                    <p className="text-brand-text-secondary text-[1.0625rem] leading-[1.75]">
                      {step.desc}
                    </p>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* Case Studies */}
        <section className="py-16 md:py-20">
          <div className="container-main">
            <ScrollReveal>
              <div className="flex items-center gap-3 mb-10 md:mb-12">
                <div className="w-1 h-8 bg-brand-gold rounded-full" />
                <h2 className="text-white font-display font-bold text-[1.5rem] md:text-[2.125rem] leading-[1.4]">
                  真實案例與成果
                </h2>
              </div>
            </ScrollReveal>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {[
                {
                  client: "40 歲科技業主管",
                  pain: "收入高但資產分散在股票、基金與定存，沒有整體配置邏輯，市場波動時焦慮。",
                  solution: "建立五艙配置（生活 / 緊急 / 保障 / 投資 / 長期），設定再平衡規則。",
                  result: "投資波動率降低 40%，緊急預備金充足，長期目標進度從模糊變清晰。",
                  tag: "資金分艙",
                },
                {
                  client: "夫妻雙薪家庭",
                  pain: "家庭月收入 18 萬，但年底總是存不到錢，不清楚錢花去哪裡。",
                  solution: "建立現金流地圖，區分必要支出、彈性支出與儲蓄投資比例。",
                  result: "每月固定儲蓄率從 5% 提升至 22%，一年累積 47 萬預備金與投資本金。",
                  tag: "現金流管理",
                },
                {
                  client: "55 歲準退休者",
                  pain: "退休後預計每月需 8 萬生活費，但現金流來源單一，擔心 longevity risk。",
                  solution: "設計三桶金現金流（定存利息 / 配息標的 / 彈性變現），建立緩衝機制。",
                  result: "退休現金流覆蓋率 100%，並保留 3 年緩衝區，退休安心度大幅提升。",
                  tag: "退休現金流",
                },
              ].map((c, index) => (
                <ScrollReveal key={c.tag} delay={index * 120}>
                  <div className="bg-brand-card border border-brand-border rounded-2xl p-6 md:p-8 transition-all duration-300 hover:border-brand-gold/50 h-full flex flex-col">
                    <div className="flex items-center gap-3 mb-5">
                      <span className="inline-block border border-brand-gold/40 text-brand-gold text-[0.875rem] font-medium px-3 py-1 rounded-full">
                        {c.tag}
                      </span>
                    </div>
                    <p className="text-white font-semibold text-[1.125rem] md:text-[1.25rem] mb-4">
                      {c.client}
                    </p>

                    <div className="space-y-4 mb-6 flex-1">
                      <div>
                        <p className="text-brand-gold/80 text-[0.9375rem] font-medium mb-1">面臨問題</p>
                        <p className="text-brand-text-secondary text-[1rem] md:text-[1.0625rem] leading-[1.7]">
                          {c.pain}
                        </p>
                      </div>
                      <div>
                        <p className="text-brand-gold/80 text-[0.9375rem] font-medium mb-1">解決方案</p>
                        <p className="text-brand-text-secondary text-[1rem] md:text-[1.0625rem] leading-[1.7]">
                          {c.solution}
                        </p>
                      </div>
                    </div>

                    <div className="border-t border-brand-border pt-5 mt-auto">
                      <p className="text-brand-gold font-bold text-[1.0625rem] md:text-[1.125rem] leading-[1.7]">
                        {c.result}
                      </p>
                    </div>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* Toolkit */}
        <section className="py-16 md:py-20">
          <div className="container-main">
            <ScrollReveal>
              <div className="flex items-center gap-3 mb-10 md:mb-12">
                <div className="w-1 h-8 bg-brand-gold rounded-full" />
                <h2 className="text-white font-display font-bold text-[1.5rem] md:text-[2.125rem] leading-[1.4]">
                  下載資產配置工具包
                </h2>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={100}>
              <div className="bg-brand-black border border-brand-border rounded-2xl p-6 md:p-10">
                <p className="text-brand-text-secondary text-[1.0625rem] md:text-[1.1875rem] leading-[1.75] mb-6 max-w-[720px]">
                  內含資金分艙表、風險承受度測驗、再平衡範本，填寫後可預約諮詢，協助建立配置與風控規則。
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <button className="inline-flex items-center justify-center gap-2 bg-brand-gold text-brand-black px-8 py-3.5 rounded-lg text-[1.0625rem] font-semibold hover:bg-brand-gold-soft transition-colors whitespace-nowrap cursor-pointer">
                    <i className="ri-download-line" />
                    下載工具包
                  </button>
                  <a
                    href="https://lin.ee/o51JBHX"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 border border-brand-border text-white px-8 py-3.5 rounded-lg text-[1.0625rem] font-medium hover:border-brand-gold hover:text-brand-gold transition-colors whitespace-nowrap"
                  >
                    <i className="ri-line-fill" />
                    加入 LINE 預約諮詢
                  </a>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </section>

        {/* FAQ Quick Q&A */}
        <section className="py-16 md:py-20 bg-brand-card border-y border-brand-border">
          <div className="container-main">
            <ScrollReveal>
              <div className="flex items-center gap-3 mb-10 md:mb-12">
                <div className="w-1 h-8 bg-brand-gold rounded-full" />
                <h2 className="text-white font-display font-bold text-[1.5rem] md:text-[2.125rem] leading-[1.4]">
                  常見問題
                </h2>
              </div>
            </ScrollReveal>

            <ScrollReveal>
              <div className="max-w-[800px] space-y-4">
                {[
                  {
                    q: "資產配置需要多少錢才能開始？",
                    a: "沒有最低門檻。重點是先建立正確的觀念與配置邏輯，即使資產不多，也能養成良好的財務習慣。",
                  },
                  {
                    q: "你們會推薦具體的投資商品嗎？",
                    a: "財產門神的重點是「配置邏輯」與「風險控管」，我們會建議適合的資產類別與比例，但不推銷特定金融商品。",
                  },
                  {
                    q: "市場下跌時該怎麼辦？",
                    a: "我們會在規劃階段就設定「再平衡規則」與「停損停利機制」，讓你依照紀律操作，不因情緒做出錯誤決策。",
                  },
                  {
                    q: "多久需要檢視一次資產配置？",
                    a: "建議每半年至一年做一次全面檢視，若遇到重大市場波動或人生階段改變（如結婚、購屋），則應即時調整。",
                  },
                ].map((faq, i) => (
                  <details
                    key={i}
                    className="bg-brand-black border border-brand-border rounded-xl group transition-all duration-300 hover:border-brand-gold/40"
                  >
                    <summary className="flex items-start justify-between gap-4 p-5 md:p-6 text-left cursor-pointer list-none">
                      <div className="flex items-start gap-3 md:gap-4">
                        <span className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-full bg-brand-gold/15 text-brand-gold font-bold text-[0.875rem] md:text-[1rem] shrink-0 mt-0.5">
                          Q{i + 1}
                        </span>
                        <span className="text-white text-[1.0625rem] md:text-[1.1875rem] font-medium leading-[1.55] pt-1">
                          {faq.q}
                        </span>
                      </div>
                      <span className="w-6 h-6 flex items-center justify-center text-brand-gold shrink-0 mt-1 group-open:rotate-180 transition-transform duration-300">
                        <i className="ri-arrow-down-s-line text-xl" />
                      </span>
                    </summary>
                    <div className="px-5 md:px-6 pb-5 md:pb-6 pl-16 md:pl-[4.25rem]">
                      <p className="text-brand-text-secondary text-[1.0625rem] md:text-[1.125rem] leading-[1.75]">
                        {faq.a}
                      </p>
                    </div>
                  </details>
                ))}
              </div>
            </ScrollReveal>
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20dark%20luxury%20background%20with%20golden%20wealth%20symbols%20and%20flowing%20data%20streams%20converging%20into%20a%20bright%20center%20on%20deep%20black%20canvas%2C%20subtle%20golden%20balance%20scale%20and%20growth%20chart%20motifs%2C%20premium%20asset%20management%20brand%20aesthetic%2C%20powerful%20prosperity%20atmosphere%20with%20warm%20gold%20accents%2C%20cinematic%20lighting%2C%204K%20quality&width=1920&height=500&seq=210&orientation=landscape"
              alt="財產門神 CTA 背景"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-black/80" />
          </div>
          <div className="container-main relative z-10">
            <ScrollReveal className="text-center max-w-[700px] mx-auto">
              <h2 className="text-white font-display font-bold text-[1.5rem] md:text-[2.125rem] leading-[1.4] mb-3">
                先守風險，後追報酬。
              </h2>
              <p className="text-brand-text-secondary text-[1.1875rem] leading-[1.75] mb-8">
                預約 30–45 分鐘資產健檢，建立你的配置與風控規則。
              </p>
              <a
                href="https://lin.ee/o51JBHX"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-brand-gold text-brand-black px-10 py-4 rounded-full text-[1.125rem] font-semibold hover:bg-brand-gold-soft transition-colors whitespace-nowrap"
              >
                <i className="ri-line-fill" />
                加入 LINE 預約資產評估
              </a>
            </ScrollReveal>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}